// src/pages/ForumMsgPage.js
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './ForumMsgPage.css';


import {
  sendPlatformMessageData,
  subscribeToInboxMergedData,
  markConversationReadData,
  hideConversationForMeData, // ✅ HINZUFÜGEN
} from "../services/messagesDataService";

import { getUserProfileData } from "../services/profileDataService";
import { subscribeToForumBadgeData } from "../services/forumDataService";
import { useAuth } from "../auth/AuthProvider";

import GateIcon from '../components/GateIcon';
import LoginHintModal from '../components/LoginHintModal';
import ContentActionMenu from "../components/ContentActionMenu";
import SpeechIcon from "../components/SpeechIcon";
import { useBlockedUsers } from "../hooks/useBlockedUsers";

const SEEN_REPLIES_KEY = "forum_seen_replies_v1";

const readSeenRepliesMap = () => {
  try {
    return JSON.parse(localStorage.getItem(SEEN_REPLIES_KEY) || "{}");
  } catch {
    return {};
  }
};


function ForumMsgPage() {
  const navigate = useNavigate();

  const { user } = useAuth();
  const myUid = user?.uid || null;
  const myEmail = user?.email || '';
  const isVerified = user?.emailVerified === true;
const {
  filterBlockedThreadList,
  addBlockedUser,
  isBlocked,
  blockedUserIds,
} = useBlockedUsers(myUid, isVerified);

  const [showLoginHint, setShowLoginHint] = useState(false);

  // 🔥 Alle Nachrichten (Inbox+Sent gemerged aus firebase.js)
  const [allMessages, setAllMessages] = useState([]);

  // 👤 Namen-Cache (uid -> { name, email })
  const [profileCache, setProfileCache] = useState({});

  // 🧵 UI
  const [selectedPeerId, setSelectedPeerId] = useState(null);
  const [reply, setReply] = useState('');

  const [chatToDelete, setChatToDelete] = useState(null); // peerUid
  const [showConfirmation, setShowConfirmation] = useState(false);

  const scrollEndRef = useRef(null);
  
const [actionToast, setActionToast] = useState({ visible: false, text: "" });
const actionToastTimerRef = useRef(null);

const showActionToast = (text) => {
  if (actionToastTimerRef.current) {
    clearTimeout(actionToastTimerRef.current);
  }

  setActionToast({ visible: true, text });

  actionToastTimerRef.current = setTimeout(() => {
    setActionToast({ visible: false, text: "" });
  }, 2200);
};

useEffect(() => {
  return () => {
    if (actionToastTimerRef.current) {
      clearTimeout(actionToastTimerRef.current);
    }
  };
}, []);

  // 🔴 Dropdown-Badges
  const [unreadCount, setUnreadCount] = useState(0);
  const [seenRepliesMap] = useState(() => readSeenRepliesMap());
  const [myReplyActivityMap, setMyReplyActivityMap] = useState({});
  const [abosReplyActivityMap, setAbosReplyActivityMap] = useState({});

  // 🔐 Login prüfen
  useEffect(() => {
    if (!myUid) setShowLoginHint(true);
  }, [myUid]);

  // ✅ Timestamp -> millis
  const toMillis = (m) => {
  if (!m) return 0;

  if (typeof m._ts === "number") return m._ts;

  const ts = m.createdAtClient || m.createdAt || m.updatedAtClient || m.updatedAt;

  if (!ts) return 0;

  if (typeof ts === "number") return ts;

  if (typeof ts === "string") {
    const parsed = Date.parse(ts);
    return Number.isNaN(parsed) ? 0 : parsed;
  }

  if (typeof ts.toMillis === "function") return ts.toMillis();

  if (typeof ts.seconds === "number") return ts.seconds * 1000;

  return 0;
};

const formatMessageDateTime = (message) => {
  const millis = toMillis(message);
  if (!millis) return "";

  return new Date(millis).toLocaleString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const buildConversationReportSnapshot = (peerId, peerLabel) => {
  if (!peerId || !myUid) {
    return "Kein Chatverlauf verfügbar.";
  }

  const conversationMessages = allMessages
    .filter((m) => {
      const a = m.senderId === myUid && m.recipientId === peerId;
      const b = m.senderId === peerId && m.recipientId === myUid;
      return a || b;
    })
    .map((m) => ({
      ...m,
      ts: toMillis(m),
    }))
    .sort((a, b) => (a.ts || 0) - (b.ts || 0));

  if (conversationMessages.length === 0) {
    return "Kein Text-Snapshot vorhanden.";
  }

  const lastMessages = conversationMessages.slice(-10);

  const lines = lastMessages.map((m) => {
    const mine = m.senderId === myUid;
    const senderLabel = mine ? "Meldender Nutzer" : peerLabel || "Gemeldeter Nutzer";
    const time = formatMessageDateTime(m) || "Zeit unbekannt";
    const text = (m.text || "").toString().trim() || "[Leere Nachricht]";

    return `[${time}] ${senderLabel}: ${text}`;
  });

  return [
    `Chat-Auszug zwischen meldendem Nutzer und ${peerLabel || "gemeldetem Nutzer"}:`,
    "",
    ...lines,
  ].join("\n");
};

 useEffect(() => {
  let unsub = null;
  let mounted = true;

  const initMessages = async () => {
    if (!myUid) {
      setAllMessages([]);
      setUnreadCount(0);
      return;
    }

    try {
      unsub = await subscribeToInboxMergedData(myUid, (merged) => {
        if (!mounted) return;

        const list = Array.isArray(merged) ? merged : [];
        setAllMessages(list);

        const c = list.reduce((acc, m) => {
  const senderId = m?.senderId || "";
  const isUnread = m?.recipientId === myUid && m?.read === false;

  if (!isUnread) return acc;

  // ✅ blockierte Absender nicht zählen
  if (senderId && isBlocked(senderId)) return acc;

  return acc + 1;
}, 0);

        setUnreadCount(c);
      });
    } catch (err) {
      console.warn("⚠️ subscribeToInboxMergedData Fehler (ForumMsgPage):", err);
      if (!mounted) return;
      setAllMessages([]);
      setUnreadCount(0);
    }
  };

  initMessages();

  return () => {
    mounted = false;
    if (typeof unsub === "function") {
      unsub();
    }
  };
}, [myUid, isBlocked, blockedUserIds]);

useEffect(() => {
  let unsub = null;
  let mounted = true;

  const init = async () => {
    if (!myUid) {
      setMyReplyActivityMap({});
      setAbosReplyActivityMap({});
      return;
    }

    try {
      unsub = await subscribeToForumBadgeData(myUid, ({ myReplies, abosReplies }) => {
        if (!mounted) return;

        setMyReplyActivityMap(myReplies || {});
        setAbosReplyActivityMap(abosReplies || {});
      });
    } catch (err) {
      console.warn("ForumMsgPage: subscribeToForumBadgeData fehlgeschlagen:", err);
      if (mounted) {
        setMyReplyActivityMap({});
        setAbosReplyActivityMap({});
      }
    }
  };

  init();

  return () => {
    mounted = false;
    if (typeof unsub === "function") unsub();
  };
}, [myUid]);

const myUnreadReplyCount = Object.values(myReplyActivityMap).reduce((acc, meta) => {
  const postId = meta?.postId;
  const latestTs = Number(meta?.latestTs || 0);
  const lastSeen = Number(seenRepliesMap[postId] || 0);
  const latestAuthorId = meta?.latestAuthorId || "";

  if (!postId) return acc;
  if (!latestTs || latestTs <= lastSeen) return acc;
  if (!latestAuthorId || latestAuthorId === myUid) return acc;

  // ✅ Antworten von blockierten Nutzern nicht zählen
  if (isBlocked(latestAuthorId)) return acc;

  return acc + 1;
}, 0);

const abosUnreadReplyCount = Object.values(abosReplyActivityMap).reduce((acc, meta) => {
  const postId = meta?.postId;
  const latestTs = Number(meta?.latestTs || 0);
  const lastSeen = Number(seenRepliesMap[postId] || 0);
  const latestAuthorId = meta?.latestAuthorId || "";

  if (!postId) return acc;
  if (!latestTs || latestTs <= lastSeen) return acc;
  if (!latestAuthorId || latestAuthorId === myUid) return acc;

  // ✅ Antworten von blockierten Nutzern nicht zählen
  if (isBlocked(latestAuthorId)) return acc;

  return acc + 1;
}, 0);

const dropdownKey = `${myUid || "guest"}-${myUnreadReplyCount}-${abosUnreadReplyCount}-${unreadCount}`;

  // ✅ Threads nach Peer gruppieren
  const threads = useMemo(() => {
    if (!myUid) return [];

    const map = new Map();

    for (const m of allMessages) {
      const senderId = m.senderId || '';
      const recipientId = m.recipientId || '';
      if (!senderId || !recipientId) continue;

      const peerId = senderId === myUid ? recipientId : senderId;
      if (!peerId) continue;
// ✅ blockierte Peers gar nicht erst in Threads aufnehmen
if (isBlocked(peerId)) continue;
      const ts = toMillis(m);
      const text = m.text || '';
      const isUnread = recipientId === myUid && m.read === false;

      const prev = map.get(peerId);
      if (!prev) {
        map.set(peerId, {
          peerId,
          lastTs: ts,
          lastText: text,
          unreadCount: isUnread ? 1 : 0,
        });
      } else {
        if (ts > (prev.lastTs || 0)) {
          prev.lastTs = ts;
          prev.lastText = text;
        }
        if (isUnread) prev.unreadCount += 1;
      }
    }
    

    const list = Array.from(map.values());
    list.sort((a, b) => {
      const au = a.unreadCount > 0 ? 1 : 0;
      const bu = b.unreadCount > 0 ? 1 : 0;
      if (bu !== au) return bu - au;
      return (b.lastTs || 0) - (a.lastTs || 0);
    });

    return list;
  }, [allMessages, myUid, isBlocked, blockedUserIds]);

  // ✅ Blockierte Nutzer/Threads ausblenden
const visibleThreads = useMemo(() => {
  return filterBlockedThreadList(threads);
}, [threads, filterBlockedThreadList]);

  // ✅ Profile der Peers laden
  useEffect(() => {
    const loadMissingProfiles = async () => {
      if (!myUid) return;

      const peerIds = Array.from(new Set(threads.map((t) => t.peerId).filter(Boolean)));
      const missing = peerIds.filter((uid) => !profileCache[uid]);

      if (missing.length === 0) return;

      try {
        const results = await Promise.all(
          missing.map(async (uid) => {
            const p = await getUserProfileData(uid);
            return [uid, { name: p?.name || '', email: p?.email || '' }];
          })
        );

        setProfileCache((prev) => {
          const next = { ...prev };
          results.forEach(([uid, data]) => (next[uid] = data));
          return next;
        });
      } catch (err) {
        console.warn('⚠️ Profil-Lookup fehlgeschlagen:', err);
      }
    };

    loadMissingProfiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threads, myUid]);

  const getPeerLabel = (peerId) => {
  if (peerId === "system" || peerId === "system-welcome") {
    return "Edens Ernte";
  }

  const p = profileCache[peerId];

  if (p?.name) return p.name;
  if (p?.email) return p.email;

  const relatedMessage = allMessages.find(
    (m) =>
      m.senderId === peerId ||
      m.recipientId === peerId
  );

  if (relatedMessage) {
    const fallbackName =
      relatedMessage.senderId === peerId
        ? relatedMessage.senderName
        : relatedMessage.recipientName;

    if (fallbackName?.trim()) {
      return fallbackName;
    }
  }

  return "Edens-Ernte Nutzer";
};

  const isSystemThread = (peerId) => {
    return peerId === "system" || peerId === "system-welcome";
  };

  // ✅ Nachrichten im selektierten Chat
  const selectedMessages = useMemo(() => {
    if (!selectedPeerId || !myUid) return [];

    const list = allMessages
      .filter((m) => {
        const a = m.senderId === myUid && m.recipientId === selectedPeerId;
        const b = m.senderId === selectedPeerId && m.recipientId === myUid;
        return a || b;
      })
      .map((m) => ({ ...m, ts: toMillis(m) }))
      .sort((a, b) => (a.ts || 0) - (b.ts || 0));

    return list;
  }, [allMessages, selectedPeerId, myUid]);

  // ✅ Scroll ans Ende
  useEffect(() => {
    if (!selectedPeerId) return;
    if (scrollEndRef.current) {
      scrollEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [selectedMessages.length, selectedPeerId]);

  const handleDropdownChange = (e) => {
    const value = e.target.value;
    if (value === 'all') navigate('/forum');
    if (value === 'my') navigate('/forum/my');
    if (value === 'abos') navigate('/forum/abos');
    if (value === 'messages') navigate('/forum/messages');
  };

  const openThread = async (peerId) => {
    setSelectedPeerId(peerId);
    setReply('');

    try {
  await markConversationReadData(myUid, peerId);

  setAllMessages((prev) =>
    prev.map((m) =>
      m.senderId === peerId && m.recipientId === myUid
        ? { ...m, read: true }
        : m
    )
  );
} catch (err) {
  console.warn("⚠️ markConversationRead fehlgeschlagen:", err);
}
  };

  const handleSendReply = async () => {
    if (isSystemThread(selectedPeerId)) return;

    const text = reply.trim();
    if (!text || !selectedPeerId) return;

    if (!myUid) {
      setShowLoginHint(true);
      return;
    }

    try {
      const recipientName = getPeerLabel(selectedPeerId);

      await sendPlatformMessageData({
        senderId: myUid,
        recipientId: selectedPeerId,
        senderEmail: myEmail || '',
        recipientEmail: profileCache[selectedPeerId]?.email || '',
        senderName: '',
        recipientName,
        text,
        contextType: 'directMessage',
        contextId: null,
      });

      setReply('');
      await markConversationReadData(myUid, selectedPeerId);
      setShowConfirmation(true);
    } catch (err) {
      console.error('Fehler beim Senden der Antwort:', err);
      alert('Die Nachricht konnte nicht gesendet werden. Bitte versuche es später erneut.');
    }
  };

  const deleteThread = async (peerId) => {
    if (!myUid || !peerId) return;

    try {
      await hideConversationForMeData(myUid, peerId);
    } catch (err) {
      console.warn('⚠️ hideConversationForMe fehlgeschlagen:', err);
    }

    if (selectedPeerId === peerId) {
      setSelectedPeerId(null);
      setReply('');
    }
    setChatToDelete(null);
  };

  return (
    <div className="forum-container forum-msg-page">
      {showLoginHint && <LoginHintModal onClose={() => setShowLoginHint(false)} />}

      <img
        src="/logo-edens-ernte-transparent.png"
        alt="Logo"
        className="browse-logo"
        onClick={() => navigate('/')}
      />
      <div className="page-background" />
      <SpeechIcon
  className="speech-icon no-tap-highlight"
  unreadCount={unreadCount}
  myUnreadReplyCount={myUnreadReplyCount}
  abosUnreadReplyCount={abosUnreadReplyCount}
  forceShowBadge={true}
/>

<GateIcon className="login-icon" />

<img
  src="/rezept-icon.png"
  alt="Rezepthelfer Icon"
  className="rezept-icon"
  onClick={() => navigate('/rezepte')}
/>
      <p className="top-claim">Zusammen wachsen - im Forum!</p>

      <div className="forum-main-box">
        {actionToast.visible && (
  <div className="mainbox-toast">
    {actionToast.text}
  </div>
)}
        <div className="forum-dropdown">
          <div className="custom-select-wrapper" key={`forum-msg-dropdown-${dropdownKey}`}>
          <select
            value="messages"
           onChange={handleDropdownChange}
            key={`forum-msg-select-${dropdownKey}`}
            >
              <option value="all">Alle Beiträge</option>
              <option value="my">{myUnreadReplyCount > 0 ? "🔴" : ""} Meine Beiträge</option>
              <option value="abos">{abosUnreadReplyCount > 0 ? "🔴" : ""} Abonnierte Beiträge</option>
              <option value="messages">{unreadCount > 0 ? "🔴" : ""} Nachrichten</option>
            </select>
            <span className="arrow-down">▼</span>
          </div>
        </div>

        <div className="message-list">
          {visibleThreads.length === 0 ? (
  <p style={{ opacity: 0.8, textAlign: 'center' }}>Noch keine Nachrichten.</p>
) : (
  visibleThreads.map((t) => {
              const label = getPeerLabel(t.peerId);
              const preview =
                (t.lastText || '').length > 40 ? t.lastText.slice(0, 40) + '…' : (t.lastText || '');
              const hasUnread = t.unreadCount > 0;

              return (
                <div className="message-row" key={t.peerId}>
                  {hasUnread && <span className="unread-icon">❗</span>}

                  <div
                    className={`message-preview ${selectedPeerId === t.peerId ? 'active' : ''}`}
                    onClick={() => openThread(t.peerId)}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="message-preview-content">
                      <div className="message-preview-name">{label}</div>
                      <div className="message-preview-text">{preview}</div>
                    </div>

                    <div
  className="msg-actions"
  onClick={(e) => e.stopPropagation()}
>
  {!isSystemThread(t.peerId) && (
    <ContentActionMenu
  contentType="directMessage"
  contentId={`conversation_${myUid}_${t.peerId}`}
  parentId={null}

  ownerId={t.peerId}
  ownerName={label}

  contentTitle={`Nachrichtenverlauf mit ${label}`}
  contentText={buildConversationReportSnapshot(t.peerId, label)}

  currentUser={user}

  reportLabel="Nachricht melden"
  blockLabel="Nutzer blockieren"

  showShare={false}
  showReport={true}
  showBlock={true}

  shareTitle={`Nachrichtenverlauf mit ${label}`}
  shareText={buildConversationReportSnapshot(t.peerId, label)}
  shareUrl="/forum/messages"

  onLoginRequired={() => setShowLoginHint(true)}
  onActionToast={showActionToast}
  onBlockedUser={(blockedUserId) => {
    addBlockedUser(blockedUserId);

    if (selectedPeerId === blockedUserId) {
      setSelectedPeerId(null);
      setReply("");
    }
  }}
/>
  )}

  <span
    className="msg-delete-icon"
    onClick={(e) => {
      e.stopPropagation();
      setChatToDelete(t.peerId);
    }}
    title="Chatverlauf löschen (nur für mich)"
  >
    🗑️
  </span>
</div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {selectedPeerId && (
        <div className="modal-overlay" onClick={() => setSelectedPeerId(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Nachrichtenverlauf mit {getPeerLabel(selectedPeerId)}</h3>

            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 10,
                maxHeight: 380,
                overflowY: 'auto',
              }}
            >
              {selectedMessages.length === 0 ? (
                <p style={{ opacity: 0.8, textAlign: 'center' }}>Noch keine Nachrichten in diesem Verlauf.</p>
              ) : (
                selectedMessages.map((m) => {
                  const mine = m.senderId === myUid;
                  return (
                    <div
  key={m.id}
  style={{
    alignSelf: mine ? "flex-end" : "flex-start",
    maxWidth: "85%",
    display: "flex",
    flexDirection: "column",
    alignItems: mine ? "flex-end" : "flex-start",
  }}
>
  <div
    className={`message-time-label ${mine ? "mine" : "theirs"}`}
  >
    {formatMessageDateTime(m)}
  </div>

  <div
    className={`message-bubble ${mine ? "mine" : "theirs"}`}
  >
    {m.text}
  </div>
</div>
                  );
                })
              )}
              <div ref={scrollEndRef} />
            </div>

            {!isSystemThread(selectedPeerId) ? (
              <>
                <textarea
                  placeholder="Antwort schreiben..."
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  style={{ marginTop: 12 }}
                />

                <div className="modal-buttons">
                  <button className="green-button" onClick={handleSendReply}>
                    Antwort senden
                  </button>

                  <button className="cancel-btn" onClick={() => setSelectedPeerId(null)}>
                    Schließen
                  </button>
                </div>
              </>
            ) : (
              <div className="modal-buttons" style={{ marginTop: 12 }}>
                <button className="cancel-btn" onClick={() => setSelectedPeerId(null)}>
                  Schließen
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {chatToDelete && (
        <div className="modal-overlay" onClick={() => setChatToDelete(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Chatverlauf wirklich löschen?</h3>
            <p>Der Verlauf wird nur für dich ausgeblendet. Der andere Nutzer behält alle Nachrichten.</p>
            <div className="modal-buttons">
              <button className="delete-btn" onClick={() => deleteThread(chatToDelete)}>
                Löschen
              </button>
              <button className="cancel-btn" onClick={() => setChatToDelete(null)}>
                Abbrechen
              </button>
            </div>
          </div>
        </div>
      )}

      {showConfirmation && (
        <div className="modal-overlay" onClick={() => setShowConfirmation(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Nachricht erfolgreich gesendet!</h3>
            <p>Der Empfänger kann dir bald antworten.</p>
            <div className="modal-buttons">
              <button className="green-button" onClick={() => setShowConfirmation(false)}>
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ForumMsgPage;